import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Sparkles, MessageSquare, Target, AlertTriangle, TrendingUp, Lightbulb } from 'lucide-react';
import { DeepInsights } from '../../services/analyticsApi';

interface DeepInsightsCardProps {
  insights: DeepInsights;
}

export const DeepInsightsCard: React.FC<DeepInsightsCardProps> = ({ insights }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          Deep AI Insights
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary */}
        {insights.conversation_summary && (
          <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
            <p className="text-sm text-gray-800 leading-relaxed">{insights.conversation_summary}</p>
          </div>
        )}

        {/* Key Topics / Themes */}
        {(insights.key_topics && insights.key_topics.length > 0) && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Key Topics
            </h4>
            <div className="flex flex-wrap gap-2">
              {insights.key_topics.map((topic: string, idx: number) => (
                <Badge key={idx} variant="secondary">
                  {topic}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Conversation Topics */}
        {insights.conversation_themes && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Conversation Topics</h4>
            
            {insights.conversation_themes.primary_theme && (
              <div>
                <p className="text-xs text-gray-600 mb-1">Primary Theme</p>
                <Badge variant="default" className="text-xs">{insights.conversation_themes.primary_theme}</Badge>
              </div>
            )}

            {insights.conversation_themes.secondary_themes && insights.conversation_themes.secondary_themes.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-600 mb-1">Secondary Themes</p>
                <div className="flex flex-wrap gap-1">
                  {insights.conversation_themes.secondary_themes.map((theme: string, idx: number) => (
                    <Badge key={idx} variant="outline" className="text-xs">{theme}</Badge>
                  ))}
                </div>
              </div>
            )}
            {/* topic_transitions not in API type; omitted */}
          </div>
        )}

        {/* Key Insights */}
        {insights.key_insights && insights.key_insights.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-yellow-600" />
              Key Insights
            </h4>
            <ul className="space-y-2">
              {insights.key_insights.map((insight, idx) => (
                <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="text-purple-600 mt-0.5">•</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recommendations */}
        {insights.recommended_actions && insights.recommended_actions.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Target className="h-4 w-4 text-green-600" />
              Recommendations
            </h4>
            <ul className="space-y-2">
              {insights.recommended_actions.map((rec: string, idx: number) => (
                <li key={idx} className="text-sm text-gray-700 flex items-start gap-2 p-2 bg-green-50 rounded">
                  <TrendingUp className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Risk Assessment */}
        {insights.risk_assessment && (
          <div className="pt-3 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              Risk Assessment
            </h4>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="text-xs text-gray-600">Risk Level</span>
                <Badge 
                  variant={
                    insights.risk_assessment.risk_level === 'high' ? 'destructive' :
                    insights.risk_assessment.risk_level === 'medium' ? 'default' :
                    'secondary'
                  }
                  className="text-xs capitalize"
                >
                  {insights.risk_assessment.risk_level}
                </Badge>
              </div>

              {insights.risk_assessment.concerns && insights.risk_assessment.concerns.length > 0 && (
                <div>
                  <p className="text-xs text-gray-600 mb-1">Concerns</p>
                  <ul className="space-y-1">
                    {insights.risk_assessment.concerns.map((c: string, idx: number) => (
                      <li key={idx} className="text-xs text-gray-700 flex items-start gap-1">
                        <span className="text-red-600">•</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {insights.risk_assessment.flags && insights.risk_assessment.flags.length > 0 && (
                <div>
                  <p className="text-xs text-gray-600 mb-1">Flags</p>
                  <div className="flex flex-wrap gap-1">
                    {insights.risk_assessment.flags.map((f: string, idx: number) => (
                      <Badge key={idx} variant="destructive" className="text-[10px]">{f}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Engagement Metrics */}
        {insights.engagement_metrics && (
          <div className="pt-3 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Engagement Metrics</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 bg-gray-50 rounded">
                <p className="text-xs text-gray-600">Mutual Engagement</p>
                <p className="text-sm font-semibold capitalize">{String(insights.engagement_metrics.mutual_engagement)}</p>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <p className="text-xs text-gray-600">Response Balance</p>
                <p className="text-sm font-semibold capitalize">{insights.engagement_metrics.response_balance}</p>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <p className="text-xs text-gray-600">Enthusiasm Level</p>
                <p className="text-sm font-semibold capitalize">{insights.engagement_metrics.enthusiasm_level}</p>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <p className="text-xs text-gray-600">Momentum</p>
                <p className="text-sm font-semibold capitalize">{insights.engagement_metrics.conversation_momentum}</p>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Footer */}
        <div className="pt-3 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
          <span>Analyzed</span>
          <span>Series Analytics</span>
        </div>
      </CardContent>
    </Card>
  );
};

